import React, { useEffect, useRef, useState } from 'react';
import { Award, TrendingUp, Layers, ListChecks, Wrench } from 'lucide-react';
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';

// Auto-trims uniform near-white / transparent margins off an image on the client,
// so variant packs shot with different amounts of whitespace all fill their box
// the same way. Works because variant images are same-origin data URIs (canvas is
// readable). Falls back to the untouched image on any error or an all-white frame.
const TrimmedImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
}> = ({ src, alt, className, style }) => {
  const [trimmed, setTrimmed] = useState<string | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    setTrimmed(null);
    if (!src) return;
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) return;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);
        const { data } = ctx.getImageData(0, 0, w, h);
        const isBg = (i: number) =>
          data[i + 3] < 10 || (data[i] > 220 && data[i + 1] > 220 && data[i + 2] > 220);

        let top = 0, bottom = h - 1, left = 0, right = w - 1;
        const rowBg = (y: number) => {
          for (let x = 0; x < w; x++) if (!isBg((y * w + x) * 4)) return false;
          return true;
        };
        const colBg = (x: number) => {
          for (let y = 0; y < h; y++) if (!isBg((y * w + x) * 4)) return false;
          return true;
        };
        while (top < bottom && rowBg(top)) top++;
        while (bottom > top && rowBg(bottom)) bottom--;
        while (left < right && colBg(left)) left++;
        while (right > left && colBg(right)) right--;

        const cw = right - left + 1;
        const ch = bottom - top + 1;
        // Bail if trimming found nothing meaningful (all-white or barely cropped).
        if (cw < w * 0.1 || ch < h * 0.1 || (cw >= w - 2 && ch >= h - 2)) return;

        const out = document.createElement('canvas');
        out.width = cw;
        out.height = ch;
        const octx = out.getContext('2d');
        if (!octx) return;
        octx.drawImage(canvas, left, top, cw, ch, 0, 0, cw, ch);
        if (!cancelled.current) setTrimmed(out.toDataURL('image/png'));
      } catch {
        /* tainted canvas / decode error → keep original */
      }
    };
    img.src = src;
    return () => { cancelled.current = true; };
  }, [src]);

  return <img src={trimmed || src} alt={alt} className={className} style={style} />;
};

// ── Types (aligned to VariantComparisonOutput) ──────────────────────────────
interface VariantRanking {
  variant_index: number;
  variant_label: string;
  overall_score: number;
  rank: number;
}
interface WinnerData {
  variant_index: number;
  variant_label: string;
  overall_score: number;
  why_it_wins: string;
}
interface VariantDescriptor {
  variant_index: number;
  descriptor: string;
}
interface LedgerElement {
  name: string;
  type: string;
  variant_index: number;
  signed_points: number;
  bucket: string;
  present_on?: number[];
  in_noise_band?: boolean;
  reason?: string;
  is_mandatory?: boolean;
}
interface BucketData {
  name: string;
  element_count: number;
  examples?: string;
  per_variant_net: Record<string, number>;
  note?: string;
}
interface CompositeItem {
  variant_index: number;
  element_name: string;
  signed_points: number;
  reason?: string;
}
interface SupportingElement {
  name: string;
  signed_points: number;
  reason?: string;
}
interface CompositeRecommendation {
  keep: CompositeItem[];
  borrow: CompositeItem[];
  kill: CompositeItem[];
  dont_touch: string[];
  thesis: string;
}
interface Finding {
  headline: string;
  body: string;
  supporting?: SupportingElement[];
}
interface WhyLost {
  variant_index: number;
  reason: string;
}
interface ReportNarrative {
  call_headline?: string;
  summary_why?: string;
  findings?: Finding[];
  why_won?: string;
  why_lost_summary?: string;
  why_lost?: (WhyLost | string)[]; // string = legacy pre-variant_index sims
  build_implications?: string;
  strategic_trades?: string[];
  where_it_flips?: string;
  what_to_test_next?: string[];
}
interface TableStake {
  name: string;
  type: string;
  signed_points: number;
  bucket: string;
  reason?: string;
}
interface PackagingVariantComparisonData {
  variants: any[];
  ranking: VariantRanking[];
  winner: WinnerData;
  differentiating_elements?: any[];
  key_differences?: any[];
  maxdiff_total_sets?: number;
  descriptors?: VariantDescriptor[];
  ledger?: LedgerElement[];
  table_stakes?: TableStake[];
  buckets?: BucketData[];
  composite?: CompositeRecommendation | null;
  runner_up_index?: number | null;
  winner_margin?: number | null;
  noise_floor?: number;
  elements_in_band?: number;
  report_narrative?: ReportNarrative;
  audit?: { panel_n?: number; scale_min?: number; scale_max?: number };
  bucket_totals?: Record<string, number>;
  structure_net?: Record<string, number>;
  elements_measured?: number;
}
interface ContentData {
  name?: string;
  data?: { brand?: string; goal?: string; variant_images?: string[] };
  [key: string]: any;
}
interface Props {
  data: PackagingVariantComparisonData;
  contentData?: ContentData;
  isPreparingExport?: boolean;
  isPrintMode?: boolean;
}

const PackagingVariantComparisonResults: React.FC<Props> = ({ data, contentData, isPrintMode = false }) => {
  const brandName = contentData?.data?.brand || contentData?.name || 'Product';
  const variantImages = contentData?.data?.variant_images || [];

  // nginx only routes `/api/` to the backend; a root-relative image path (e.g. an
  // `/api/...` or `/rlhf/...` src) would otherwise hit the SPA and 404, blanking the
  // image. Prepend the API base to any root-relative path. Mirrors resolveImageSrc in
  // SimulationInputsDropdown (added in 8b5f484 for competitor/packaging images).
  const API_URL = import.meta.env.VITE_API_URL || '';
  const resolveImageSrc = (src: string | undefined | null): string => {
    if (!src) return '';
    // Already renderable as-is: inline base64, absolute URLs, or a path that already
    // carries the API base (guard against doubling `/api/api/...`).
    if (src.startsWith('data:') || src.startsWith('http')) return src;
    if (API_URL && src.startsWith(`${API_URL}`)) return src;
    // if (src.startsWith('/api/')) return `${API_URL.replace(/\/api$/, '')}${src}`;
    if (src.startsWith('/')) return `${API_URL}${src}`;
    return src;
  };
  const { winner, ranking } = data;
  const descriptors = data.descriptors || [];
  const ledger = (data.ledger || []).slice().sort((a, b) => b.signed_points - a.signed_points);
  const tableStakes = (data.table_stakes || []).slice().sort((a, b) => b.signed_points - a.signed_points);
  const buckets = data.buckets || [];
  const composite = data.composite || null;
  const narrative = data.report_narrative || {};

  const scoreFor = (vi: number) => ranking.find((r) => r.variant_index === vi)?.overall_score;
  const descFor = (vi: number) => descriptors.find((d) => d.variant_index === vi)?.descriptor;
  const variantIndices = (variantImages.length
    ? variantImages.map((_, i) => i + 1)
    : ranking.map((r) => r.variant_index)
  ).sort((a, b) => a - b);

  const maxAbs = Math.max(1, ...ledger.map((e) => Math.abs(e.signed_points)));
  const margin = data.winner_margin;
  const runnerUp = data.runner_up_index;
  const noiseFloor = data.noise_floor ?? 0;
  const elementsInBand = data.elements_in_band ?? 0;

  const elementsMeasured = data.elements_measured ?? (ledger.length + tableStakes.length);
  // Magnitude-scaled cell tint for the bucket matrix (green +, red −), like the target report.
  const maxBucketAbs = Math.max(
    1,
    ...buckets.flatMap((b) => Object.values(b.per_variant_net || {}).map((v) => Math.abs(v)))
  );
  const cellTint = (v: number): string => {
    if (!v) return 'transparent';
    const a = 0.06 + (Math.abs(v) / maxBucketAbs) * 0.22;
    return v > 0 ? `rgba(5,150,105,${a.toFixed(3)})` : `rgba(239,68,68,${a.toFixed(3)})`;
  };
  const findings = (narrative.findings || []).slice(0, 3);
  // Each variant gets its own identity colour, used on every V-tag across the report.
  const variantColor = (vi: number): string =>
    ['#2563EB', '#D97706', '#7C3AED', '#DB2777', '#0891B2'][(vi - 1) % 5];
  // Force coloured fills (ledger bars, noise band, cell tints, tags) to survive print/PDF — browsers
  // drop element backgrounds when printing unless print-color-adjust is exact.
  const printExact: React.CSSProperties = { printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' } as React.CSSProperties;

  return (
    <div className={`packaging-variant-report space-y-6 ${isPrintMode ? 'print-mode overflow-visible h-auto' : ''}`}>
      {/* ── Summary: variant cards + scores + descriptors ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center">
            <Award className="w-5 h-5 text-[#07E5D1] mr-2" />
            <h2 className="text-xl font-semibold text-[#1C1C1E]">Packaging Comparison — {brandName}</h2>
          </div>
          <p className="mt-2 font-mono text-xs text-[#9CA3AF]">
            <span className="text-[#1C1C1E] font-medium">{variantIndices.length}</span> variants
            {elementsMeasured > 0 && (
              <>&nbsp;·&nbsp; <span className="text-[#1C1C1E] font-medium">{elementsMeasured}</span> elements measured
              &nbsp;·&nbsp; <span className="text-[#1C1C1E] font-medium">{Math.max(0, ledger.length - elementsInBand)}</span> that move preference</>
            )}
            &nbsp;·&nbsp; <span className="text-[#1C1C1E] font-medium">1</span> recommended build
          </p>
        </div>

        {variantIndices.length > 0 && (
          <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${Math.min(variantIndices.length, 5)}, minmax(0, 1fr))` }}>
            {variantIndices.map((vi, idx) => {
              const isWinner = winner.variant_index === vi;
              const score = scoreFor(vi);
              const img = variantImages[idx];
              return (
                <div key={vi} className="flex flex-col items-center text-center w-full">
                  <div className="relative w-full">
                    {isWinner && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-[#07E5D1] text-white text-xs font-semibold rounded z-10">WINNER</span>
                    )}
                    {img && (
                      // Uniform box for every variant — the image is auto-trimmed of
                      // whitespace, then object-contain fits the cropped pack inside the
                      // SAME footprint, so variants render at a consistent size regardless
                      // of source dimensions or how much white space each shot had.
                      <TrimmedImage src={resolveImageSrc(img)} alt={`Variant ${vi}`}
                        className="w-full h-[240px] object-contain bg-white rounded-lg border border-gray-200 p-4"
                        style={{ borderTopWidth: 3, borderTopColor: variantColor(vi), ...printExact }} />
                    )}
                  </div>
                  <span className="mt-2 flex items-center gap-1.5 text-sm font-medium text-[#374151]">
                    <VTag vi={vi} color={variantColor(vi)} solid={isWinner} /> Variant {vi}
                  </span>
                  {score !== undefined && (
                    <>
                      <span className="text-2xl font-bold mt-1" style={{ color: isWinner ? variantColor(vi) : '#374151' }}>{score.toFixed(1)}</span>
                      <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">Preference Score</span>
                    </>
                  )}
                  {descFor(vi) && <p className="text-xs text-[#6B7280] mt-1.5 leading-snug">{descFor(vi)}</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* Why the winner won — one-line verdict only */}
        <div className="mt-6 pt-5 border-t border-gray-100">
          <h3 className="text-base font-semibold text-[#1C1C1E] mb-2">Why Variant {winner.variant_index} won</h3>
          <div className="text-[15px] leading-relaxed text-[#374151]">
            <MarkdownRenderer content={narrative.summary_why || narrative.why_won || winner.why_it_wins} />
          </div>
        </div>

        {/* Why the others lost — roll-up summary + per-variant detail, moved up under the images */}
        {(narrative.why_lost_summary || (narrative.why_lost && narrative.why_lost.length > 0)) && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <h3 className="text-base font-semibold text-[#1C1C1E] mb-2">Why the others lost</h3>
            {narrative.why_lost_summary && (
              <p className="text-sm text-[#374151] mb-4">{narrative.why_lost_summary}</p>
            )}
            <div className="space-y-4">
              {[...(narrative.why_lost || [])]
                // Tolerate legacy string-only entries (pre-variant_index sims) alongside the new shape.
                .map((w) => (typeof w === 'string' ? { variant_index: 0, reason: w as string } : w))
                .sort((a, b) => (scoreFor(b.variant_index) ?? 0) - (scoreFor(a.variant_index) ?? 0))
                .map((w, i) => {
                  const loserVi = w.variant_index;
                  return (
                    <div key={loserVi || i} className="border-l-4 pl-4" style={{ borderColor: loserVi ? variantColor(loserVi) : '#D1D5DB' }}>
                      {loserVi > 0 && (
                        <div className="flex items-center gap-1.5 mb-1">
                          <VTag vi={loserVi} color={variantColor(loserVi)} /> <span className="text-sm font-semibold text-[#1C1C1E]">Variant {loserVi}</span>
                        </div>
                      )}
                      <p className="text-sm text-[#374151]">{w.reason}</p>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* audit strip — real stats only; Panel size removed per request */}
        {elementsMeasured > 0 && (
        <div className="mt-6 flex flex-wrap border border-gray-200 rounded-lg divide-x divide-gray-200">
          <AuditCell k="Scale" v="0–100" />
          <AuditCell k="Elements measured" v={String(elementsMeasured)} />
          {margin != null && runnerUp != null && (
            <AuditCell k={`V${winner.variant_index} over V${runnerUp}`} v={`+${margin.toFixed(1)}`} />
          )}
        </div>
        )}
      </div>

      {/* ── Overall Pack Assessment (was "The Call"): keep / borrow / reconsider + recommendation ── */}
      {composite && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-1">
            <ListChecks className="w-5 h-5 text-[#07E5D1] mr-2" />
            <h3 className="text-lg font-semibold text-[#1C1C1E]">Overall Pack Assessment</h3>
          </div>
          {narrative.call_headline && (
            <p className="text-xl font-bold text-[#1C1C1E] leading-snug mb-1">{narrative.call_headline}</p>
          )}
          {composite.thesis && <p className="text-sm text-[#6B7280] mb-4">{composite.thesis}</p>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CompositeColumn title={`Keep from Variant ${winner.variant_index}`} items={composite.keep} color="#059669" sign="+" variantColor={variantColor} winnerIndex={winner.variant_index} />
            <CompositeColumn title="Borrow from others" items={composite.borrow} color="#07E5D1" sign="+" variantColor={variantColor} winnerIndex={winner.variant_index} />
            <CompositeColumn title="Elements to reconsider" items={composite.kill} color="#EF4444" sign="" variantColor={variantColor} winnerIndex={winner.variant_index} />
          </div>
          {composite.dont_touch?.length > 0 && (
            <p className="text-xs text-[#9CA3AF] mt-4">
              Don’t touch (moved nothing): {composite.dont_touch.join(', ')}
            </p>
          )}
          {narrative.build_implications && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-[#6B7280] mb-1">Recommendation</h4>
              <div className="text-sm text-[#374151]"><MarkdownRenderer content={narrative.build_implications} /></div>
            </div>
          )}
        </div>
      )}

      {/* ── Findings: analytic findings ── */}
      {findings.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-3">
            <Wrench className="w-5 h-5 text-[#07E5D1] mr-2" />
            <h3 className="text-lg font-semibold text-[#1C1C1E]">Findings</h3>
          </div>
          {findings.length > 0 && (
            <div className="space-y-5">
              {findings.map((f, i) => (
                <div key={i} className="border-l-4 border-l-[#07E5D1] pl-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF] mb-1">Finding {String(i + 1).padStart(2, '0')}</p>
                  <h4 className="text-base font-bold text-[#1C1C1E] mb-1 leading-snug">{f.headline}</h4>
                  <div className="text-sm text-[#374151]"><MarkdownRenderer content={f.body} /></div>
                  {f.supporting && f.supporting.length > 0 && (
                    <ul className="mt-3 space-y-1.5">
                      {f.supporting.map((s, j) => {
                        const pos = s.signed_points >= 0;
                        return (
                          <li key={j} className="flex items-baseline gap-2 text-xs">
                            <span className="font-mono font-semibold whitespace-nowrap w-12 text-right" style={{ color: pos ? '#059669' : '#EF4444' }}>
                              {pos ? '+' : ''}{s.signed_points}
                            </span>
                            <span className="text-[#374151]">
                              <span className="font-medium text-[#1C1C1E]">{s.name}</span>
                              {s.reason && <span className="text-[#6B7280]"> — {s.reason}</span>}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── The trades ── */}
      {narrative.strategic_trades && narrative.strategic_trades.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-3">
            <TrendingUp className="w-5 h-5 text-[#07E5D1] mr-2" />
            <h3 className="text-lg font-semibold text-[#1C1C1E]">The trades</h3>
          </div>
          <ol className="border-t border-gray-200">
            {narrative.strategic_trades.map((it, i) => (
              <li key={i} className="grid grid-cols-[28px_1fr] gap-3 py-3 border-b border-gray-100">
                <span className="font-mono text-xs text-[#9CA3AF] pt-0.5">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-sm text-[#374151]">{it}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* ── Where it flips ── */}
      {narrative.where_it_flips && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-3">
            <Layers className="w-5 h-5 text-[#07E5D1] mr-2" />
            <h3 className="text-lg font-semibold text-[#1C1C1E]">Where it flips</h3>
          </div>
          <p className="text-sm text-[#374151]">{narrative.where_it_flips}</p>
        </div>
      )}

      {/* ── What we'd test next: the test queue (moved to just before the ledger) ── */}
      {narrative.what_to_test_next && narrative.what_to_test_next.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-3">
            <ListChecks className="w-5 h-5 text-[#07E5D1] mr-2" />
            <h3 className="text-lg font-semibold text-[#1C1C1E]">What we’d test next</h3>
          </div>
          <ol className="border-t border-gray-200">
            {narrative.what_to_test_next.map((it, i) => (
              <li key={i} className="grid grid-cols-[28px_1fr] gap-3 py-3 border-b border-gray-100">
                <span className="font-mono text-xs text-[#9CA3AF] pt-0.5">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-sm text-[#374151]">{it}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* ── The Ledger: single signed axis (moved to the end) ── */}
      {ledger.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-1">
            <TrendingUp className="w-5 h-5 text-[#07E5D1] mr-2" />
            <h3 className="text-lg font-semibold text-[#1C1C1E]">The Ledger</h3>
          </div>
          <p className="text-sm text-[#6B7280] mb-4">
            All {ledger.length} differentiating elements on one axis, ranked. Signed points of preference — the shaded band is the noise floor; anything inside it should not move a decision.
          </p>
          {(() => {
            // Axis scale + band geometry. bandEdge = half-width of the shaded noise band as % of
            // the track (each side of the centre). noiseFloor is the median per-element band.
            const bandEdge = Math.min(50, (noiseFloor / maxAbs) * 50);
            const LEFT = 292; // name col (280) + gap (12)
            const RIGHT = 58; // value col (46) + gap (12)
            const gridCols = `280px minmax(0,1fr) 46px`;
            const tick = (v: number) => 50 + (v / maxAbs) * 50; // signed value -> % across track
            return (
              <div className="relative">
                {/* axis tick labels */}
                <div className="grid gap-3 items-end pb-1.5 border-b border-gray-300" style={{ gridTemplateColumns: gridCols }}>
                  <span />
                  <div className="relative h-4 font-mono text-[10px] text-[#9CA3AF]">
                    {[-maxAbs, -noiseFloor, 0, noiseFloor, maxAbs].map((v, k) => (
                      <span key={k} className="absolute bottom-0 -translate-x-1/2" style={{ left: `${tick(v)}%` }}>
                        {v > 0 ? '+' : ''}{v}
                      </span>
                    ))}
                  </div>
                  <span />
                </div>

                {/* rows, with the shaded noise band overlaid across the track column */}
                <div className="relative">
                  {noiseFloor > 0 && bandEdge > 0 && (
                    <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: LEFT, right: RIGHT }}>
                      <div
                        className="absolute top-0 bottom-0"
                        style={{
                          left: `${50 - bandEdge}%`,
                          right: `${50 - bandEdge}%`,
                          background: 'rgba(150,146,136,0.13)',
                          borderLeft: '1px dashed #B4B1A9',
                          borderRight: '1px dashed #B4B1A9',
                          ...printExact,
                        }}
                      />
                      <span
                        className="absolute top-1 left-1/2 -translate-x-1/2 font-mono text-[9px] uppercase tracking-widest text-[#9CA3AF] opacity-80"
                        style={{ writingMode: 'vertical-rl' }}
                      >
                        Noise ±{noiseFloor}
                      </span>
                    </div>
                  )}

                  {ledger.map((el, i) => {
                    const pos = el.signed_points >= 0;
                    const widthPct = (Math.abs(el.signed_points) / maxAbs) * 50;
                    const faded = el.in_noise_band;
                    // Small in-band values (e.g. +3 against a ±170 scale) compute to <1% width and
                    // vanish. Give every non-zero bar a minimum visible sliver.
                    const minW = el.signed_points !== 0 ? 3 : 0;
                    return (
                      <div key={i} className="grid gap-3 items-center border-b border-gray-100" style={{ gridTemplateColumns: gridCols }}>
                        <div className={`flex items-center justify-end gap-2 text-[12.5px] leading-tight py-1.5 pr-1 ${faded ? 'text-[#B4B1A9]' : 'text-[#1C1C1E]'}`}>
                          <span className="text-right">
                            {el.name}
                            {el.reason && <span className="block text-[10px] text-[#9CA3AF] leading-snug">{el.reason}</span>}
                          </span>
                          <span className="shrink-0"><VTag vi={el.variant_index} color={variantColor(el.variant_index)} solid={el.variant_index === winner.variant_index} /></span>
                        </div>
                        <div className="relative h-6">
                          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300" />
                          {/* NOTE: bar/track heights use STANDARD Tailwind classes (h-6, h-2.5, h-1),
                              never `h-[..]` arbitrary values and never inline height — the global print
                              rules match BOTH `[class*="h-["]` and `[style*="height"]` and force
                              height:auto, which collapses these absolutely-positioned bars to 0px so
                              they'd vanish in print (this was the noise-band dead zone). */}
                          <div
                            className={`absolute rounded-sm ${faded ? 'top-2 h-1' : 'top-1.5 h-2.5'}`}
                            style={faded
                              ? { backgroundColor: '#8E9198', minWidth: minW, ...printExact, ...(pos ? { left: '50%', width: `${widthPct}%` } : { right: '50%', width: `${widthPct}%` }) }
                              : { backgroundColor: pos ? '#059669' : '#EF4444', minWidth: minW, ...printExact, ...(pos ? { left: '50%', width: `${widthPct}%` } : { right: '50%', width: `${widthPct}%` }) }}
                          />
                        </div>
                        <div className={`font-mono text-[12.5px] font-medium text-right ${faded ? 'text-[#B4B1A9]' : pos ? 'text-[#059669]' : 'text-[#EF4444]'}`}>
                          {pos ? '+' : ''}{el.signed_points}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* legend */}
                <div className="flex flex-wrap gap-4 mt-4 font-mono text-[10.5px] text-[#9CA3AF]">
                  {variantIndices.map((vi) => (
                    <span key={vi} className="inline-flex items-center gap-1">
                      <VTag vi={vi} color={variantColor(vi)} solid={vi === winner.variant_index} />
                      {descFor(vi)?.split(/[.,]/)[0] || `Variant ${vi}`}
                    </span>
                  ))}
                  <span style={{ color: '#059669' }}>■ gains preference</span>
                  <span style={{ color: '#EF4444' }}>■ loses preference</span>
                  <span style={{ color: '#B4B1A9' }}>■ inside the noise floor</span>
                </div>
              </div>
            );
          })()}
          {elementsInBand > 0 && (
            <p className="text-sm text-[#6B7280] mt-4 pt-3 border-t border-gray-100">
              <span className="font-semibold text-[#1C1C1E]">{elementsInBand} of {ledger.length} elements had too small an effect to matter</span>{' '}
              — shoppers didn&rsquo;t clearly prefer or reject them, so they&rsquo;re shown for completeness but don&rsquo;t drive the recommendation. The other {ledger.length - elementsInBand} moved preference enough to count, and those are what the decision is based on.
            </p>
          )}
        </div>
      )}

      {/* ── Common to all variants (table-stakes) ── */}
      {tableStakes.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-1">
            <ListChecks className="w-5 h-5 text-[#9CA3AF] mr-2" />
            <h3 className="text-lg font-semibold text-[#1C1C1E]">Common to all variants</h3>
          </div>
          <p className="text-sm text-[#6B7280] mb-4">
            On every pack, so they don’t differentiate the variants and are kept out of the ledger, composite and spread — listed here for reference.
          </p>
          <div className="flex flex-wrap gap-2">
            {tableStakes.map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-xs text-[#374151]">
                <span className="truncate max-w-[220px]">{t.name}</span>
                {t.reason
                  ? <span className="text-[10px] text-[#9CA3AF]">— {t.reason}</span>
                  : <span className="text-[10px] text-[#9CA3AF] uppercase">{t.bucket}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── By Bucket: matrix ── */}
      {buckets.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-1">
            <Layers className="w-5 h-5 text-[#07E5D1] mr-2" />
            <h3 className="text-lg font-semibold text-[#1C1C1E]">By Bucket</h3>
          </div>
          <p className="text-sm text-[#6B7280] mb-4">Net signed effect per bucket — direction only. Read columns for a variant’s profile, rows for where the fight was.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#6B7280] border-b-2 border-gray-300">
                  <th className="text-left py-2 pr-3 font-medium align-bottom">Bucket</th>
                  {variantIndices.map((vi) => (
                    <th key={vi} className="text-center py-2 px-3 font-medium align-bottom">
                      <VTag vi={vi} color={variantColor(vi)} solid={winner.variant_index === vi} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {buckets.map((b) => (
                  <tr key={b.name} className="border-b border-gray-100">
                    <td className="py-2 pr-4 align-top w-2/5">
                      <span className="font-medium text-[#1C1C1E]">{b.name}</span>
                      <span className="text-[10px] text-[#9CA3AF] ml-1">{b.element_count} el</span>
                    </td>
                    {variantIndices.map((vi) => {
                      const v = b.per_variant_net[String(vi)] ?? b.per_variant_net[vi as any] ?? 0;
                      return (
                        <td key={vi} className="text-center py-2 px-3 font-semibold" style={{ backgroundColor: cellTint(v), ...printExact }}>
                          <span style={{ color: v > 0 ? '#059669' : v < 0 ? '#EF4444' : '#9CA3AF' }}>
                            {v > 0 ? '+' : ''}{v || 0}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-[#9CA3AF] mt-3">
            Net = sum of signed element effects in the bucket, direction only — not a reconstruction of the preference score.
          </p>
        </div>
      )}
    </div>
  );
};

const AuditCell: React.FC<{ k: string; v: string }> = ({ k, v }) => (
  <div className="flex-1 min-w-[120px] bg-white px-4 py-2.5">
    <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] mb-0.5">{k}</div>
    <div className="text-sm font-mono text-[#1C1C1E]">{v}</div>
  </div>
);

// Variant identity chip — ALWAYS a solid fill in the variant's own colour so each variant reads
// distinctly at a glance. The winner is marked with a ring (offset outline), not a colour change.
const VTag: React.FC<{ vi: number; color: string; solid?: boolean }> = ({ vi, color, solid }) => (
  <span
    className="inline-block font-mono text-[10px] font-semibold leading-none px-1.5 py-0.5 rounded align-middle"
    style={{
      backgroundColor: color,
      color: '#fff',
      border: `1px solid ${color}`,
      printColorAdjust: 'exact',
      WebkitPrintColorAdjust: 'exact',
      ...(solid ? { boxShadow: `0 0 0 1.5px #fff, 0 0 0 3px ${color}` } : {}),
    } as React.CSSProperties}
  >
    V{vi}
  </span>
);

const CompositeColumn: React.FC<{
  title: string; items: CompositeItem[]; color: string; sign: string;
  variantColor: (vi: number) => string; winnerIndex: number;
}> = ({ title, items, color, sign, variantColor, winnerIndex }) => (
  <div className="border border-gray-200 rounded-lg p-3">
    <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color }}>{title}</h4>
    {items.length === 0 ? (
      <p className="text-xs text-[#9CA3AF]">—</p>
    ) : (
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start justify-between gap-2 text-xs">
            <span className="text-[#374151] flex items-start gap-2">
              <span className="shrink-0"><VTag vi={it.variant_index} color={variantColor(it.variant_index)} solid={it.variant_index === winnerIndex} /></span>
              <span>
                {it.element_name}
                {it.reason && <span className="block text-[10px] text-[#9CA3AF] leading-snug">{it.reason}</span>}
              </span>
            </span>
            <span className="font-semibold whitespace-nowrap" style={{ color }}>{sign}{it.signed_points}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default PackagingVariantComparisonResults;
